import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Info,
  CheckCircle2,
  Lock,
  Unlock,
  Truck,
  Users,
  Tag,
  RefreshCw,
  Search,
  ChevronRight
} from 'lucide-react';
import { propostaSaldoService, PropostaSaldoInfo } from '../../services/propostaSaldoService';
import { propostaMetaService } from '../../services/propostaMetaService';
import { supabase } from '../../lib/supabase';

export function ConfiguracaoSaldoPropostas() {
  const [bloquearSemSaldo, setBloquearSemSaldo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);

  // Amostra de propostas para validação em tempo real
  const [propostasAmostra, setPropostasAmostra] = useState<any[]>([]);
  const [selectedPropostaId, setSelectedPropostaId] = useState<number | null>(null);
  const [selectedSaldoInfo, setSelectedSaldoInfo] = useState<PropostaSaldoInfo | null>(null);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [loadingPropostas, setLoadingPropostas] = useState(true);
  const [filtroBusca, setFiltroBusca] = useState('');

  useEffect(() => {
    carregarConfiguracao();
    carregarAmostraPropostas();
  }, []);

  const carregarConfiguracao = async () => {
    setLoading(true);
    try {
      const ativo = await propostaSaldoService.isBloqueioSemSaldoAtivo();
      setBloquearSemSaldo(ativo);
    } catch (err) {
      console.error('Erro ao carregar preferência de bloqueio:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarAmostraPropostas = async () => {
    setLoadingPropostas(true);
    try {
      const { data, error } = await supabase
        .from('pedido')
        .select('*, pessoa(id, nome, documento), detalhes_do_pedido(id, servicos, valor_total, quantidade, valor_unitario, tipo_servico_id)')
        .is('deleted_at', null)
        .order('id', { ascending: false })
        .limit(100);

      if (data && data.length > 0) {
        const enriched = data.map((p: any) => propostaMetaService.enrichProposta(p));
        setPropostasAmostra(enriched);
        handleSelecionarProposta(enriched[0].id);
      } else {
        setPropostasAmostra([]);
      }
    } catch (err) {
      console.error('Erro ao carregar propostas para teste de saldo:', err);
    } finally {
      setLoadingPropostas(false);
    }
  };

  const handleSelecionarProposta = async (id: number) => {
    setSelectedPropostaId(id);
    setLoadingSaldo(true);
    try {
      const saldo = await propostaSaldoService.getSaldoProposta(id);
      setSelectedSaldoInfo(saldo);
    } catch (err) {
      console.error('Erro ao verificar saldo da proposta de teste:', err);
    } finally {
      setLoadingSaldo(false);
    }
  };

  const handleToggleBloqueio = async (novoValor: boolean) => {
    setSaving(true);
    setFeedback(null);
    try {
      await propostaSaldoService.setBloqueioSemSaldo(novoValor);
      setBloquearSemSaldo(novoValor);
      setFeedback({
        tipo: 'sucesso',
        texto: novoValor
          ? 'Configuração salva: O sistema agora BLOQUEIA emissão de OS quando o item específico contratado estiver com saldo esgotado.'
          : 'Configuração salva: O sistema agora exibe AVISO informativo de saldo do item esgotado, sem impedir a emissão.'
      });

      setTimeout(() => setFeedback(null), 5000);
    } catch (err: any) {
      console.error('Erro ao salvar preferência de bloqueio:', err);
      setFeedback({
        tipo: 'erro',
        texto: 'Erro ao atualizar a configuração. Tente novamente.'
      });
    } finally {
      setSaving(false);
    }
  };

  const propostasFiltradas = propostasAmostra.filter((p) => {
    const clienteNome = (p.pessoa?.nome || p.pessoas?.nome || '').toLowerCase();
    const numero = String(p.numero_proposta || p.id);
    const termo = filtroBusca.toLowerCase();
    return clienteNome.includes(termo) || numero.includes(termo);
  });

  return (
    <div className="space-y-6">
      {/* Card Principal: Toggle de Bloqueio */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-2xl transition-colors shrink-0 ${
              bloquearSemSaldo ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
            }`}>
              {bloquearSemSaldo ? (
                <Lock className="w-6 h-6" />
              ) : (
                <Unlock className="w-6 h-6" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-slate-900">
                  Controle de Saldo por Item de Proposta
                </h2>
                <span
                  className={`text-2xs font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                    bloquearSemSaldo
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}
                >
                  {bloquearSemSaldo ? 'Modo Bloqueio Ativo' : 'Modo Aviso (Padrão)'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                O sistema gerencia e calcula o saldo de forma <strong>independente por item</strong> (ex: Truck, Toco, Munck, HR, Ajudantes). Se um item esgotar sua cota, os outros itens com saldo continuam liberados normalmente para emissão.
              </p>
            </div>
          </div>

          {/* Toggle Switch */}
          <div className="flex items-center gap-3 self-start md:self-center shrink-0">
            <span className="text-xs font-semibold text-slate-600">
              {bloquearSemSaldo ? 'Bloquear se Saldo = 0' : 'Apenas Alertar'}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={bloquearSemSaldo}
                disabled={loading || saving}
                onChange={(e) => handleToggleBloqueio(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-13 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[3px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedback && (
          <div
            className={`p-4 mx-6 mt-4 rounded-xl text-xs flex items-center gap-2 transition-all ${
              feedback.tipo === 'sucesso'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-rose-50 text-rose-800 border border-rose-200'
            }`}
          >
            {feedback.tipo === 'sucesso' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{feedback.texto}</span>
          </div>
        )}

        {/* Quadro Explicativo das Regras */}
        <div className="p-6 bg-slate-50/70 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-4 rounded-xl border transition-all ${
            !bloquearSemSaldo
              ? 'bg-amber-50/50 border-amber-200 shadow-2xs'
              : 'bg-white border-slate-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h4 className="text-xs font-bold text-slate-900">
                Opção 1: Apenas Avisar (Desmarcado)
              </h4>
            </div>
            <p className="text-2xs text-slate-600 leading-relaxed">
              Quando o item selecionado (veículo ou ajudante) está com saldo 0, o sistema exibe alertas claros e destacados indicando que a cota foi atingida. <strong>A criação da OS NÃO é impedida</strong>, permitindo flexibilidade operacional para cobrança posterior.
            </p>
          </div>

          <div className={`p-4 rounded-xl border transition-all ${
            bloquearSemSaldo
              ? 'bg-red-50/50 border-red-200 shadow-2xs'
              : 'bg-white border-slate-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-red-600" />
              <h4 className="text-xs font-bold text-slate-900">
                Opção 2: Bloqueio Rigoroso por Item (Marcado)
              </h4>
            </div>
            <p className="text-2xs text-slate-600 leading-relaxed">
              Se o item selecionado para a OS estiver zerado (ex: cota de Truck esgotada), o sistema <strong>bloqueia a submissão</strong> apenas para aquele item esgotado. Caso o operador selecione outro item contratado (ex: HR ou Munck) que ainda possua saldo, a emissão é permitida normalmente.
            </p>
          </div>
        </div>

        {/* Exemplo Prático de Negócio */}
        <div className="px-6 py-4 bg-slate-100/60 border-t border-slate-200 flex items-start gap-3">
          <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
          <div className="text-2xs text-slate-600 leading-relaxed">
            <strong className="text-slate-800">Regra de cálculo granular:</strong> Cada Ordem de Serviço desconta exatamente 1 unidade do <span className="font-semibold text-slate-800">item de serviço/veículo</span> selecionado e a quantidade especificada de <span className="font-semibold text-slate-800">ajudantes</span>. Ordens de Serviço com status <span className="font-semibold text-slate-700">"Cancelado"</span> são estornadas automaticamente e não consomem saldo.
          </div>
        </div>
      </div>

      {/* Simulador / Inspetor de Saldo em Tempo Real */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Truck className="w-4 h-4 text-red-600" />
              Inspetor de Saldo por Item (Simulador ao Vivo)
            </h3>
            <p className="text-2xs text-slate-500 mt-0.5">
              Visualize em tempo real como o sistema decompõe o consumo de cada item nas propostas comerciais.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={carregarAmostraPropostas}
              className="p-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-800 transition text-xs flex items-center gap-1 cursor-pointer"
              title="Recarregar Propostas"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <div className="relative max-w-xs w-full">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por cliente ou número..."
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>

        {/* Grid do Inspetor */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Coluna 1: Lista de Propostas */}
          <div className="md:col-span-1 border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {loadingPropostas ? (
              <div className="p-8 text-center text-xs text-slate-500 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-red-600" />
                <span>Carregando propostas...</span>
              </div>
            ) : propostasFiltradas.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400">
                Nenhuma proposta encontrada.
              </div>
            ) : (
              propostasFiltradas.map((prop) => (
                <button
                  key={prop.id}
                  type="button"
                  onClick={() => handleSelecionarProposta(prop.id)}
                  className={`w-full text-left p-3 text-xs transition-colors flex items-center justify-between cursor-pointer ${
                    selectedPropostaId === prop.id
                      ? 'bg-red-50 text-red-900 font-semibold'
                      : 'hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <div className="truncate pr-2">
                    <p className="font-bold truncate">{prop.pessoa?.nome || prop.pessoas?.nome || 'Cliente não informado'}</p>
                    <p className="text-2xs text-slate-400">
                      Proposta #{prop.numero_proposta || prop.id} • {prop.status || 'Ativo'}
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ${
                    selectedPropostaId === prop.id ? 'text-red-600' : 'text-slate-300'
                  }`} />
                </button>
              ))
            )}
          </div>

          {/* Coluna 2 e 3: Painel de Diagnóstico de Saldo por Item */}
          <div className="md:col-span-2 bg-slate-50 rounded-xl p-4 border border-slate-200 flex flex-col justify-between">
            {loadingSaldo ? (
              <div className="flex items-center justify-center h-64 text-xs text-slate-500 gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-red-600" />
                <span>Calculando saldo individual dos itens...</span>
              </div>
            ) : selectedSaldoInfo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                  <div>
                    <span className="text-2xs font-bold text-slate-400 uppercase">Diagnóstico da Proposta #{selectedSaldoInfo.pedidoId}</span>
                    <h4 className="text-xs font-extrabold text-slate-900">
                      {selectedSaldoInfo.clienteNome || 'Cliente'}
                    </h4>
                  </div>

                  <div className="text-right">
                    <span
                      className={`text-2xs font-bold px-2.5 py-1 rounded-full uppercase ${
                        selectedSaldoInfo.statusSaldo === 'esgotado'
                          ? 'bg-red-100 text-red-800 border border-red-200'
                          : selectedSaldoInfo.statusSaldo === 'alerta'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : selectedSaldoInfo.statusSaldo === 'sem_cota'
                          ? 'bg-slate-100 text-slate-700 border border-slate-200'
                          : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      }`}
                    >
                      {selectedSaldoInfo.statusSaldo === 'esgotado'
                        ? 'Todos Itens Esgotados'
                        : selectedSaldoInfo.statusSaldo === 'alerta'
                        ? 'Cotas em Alerta'
                        : selectedSaldoInfo.statusSaldo === 'sem_cota'
                        ? 'Sem Cota Fixa'
                        : 'Itens com Saldo'}
                    </span>
                  </div>
                </div>

                {/* Tabela de Itens Decompostos */}
                <div className="space-y-2">
                  <span className="text-2xs font-bold text-slate-600 uppercase tracking-wide">
                    Itens e Cotas do Contrato ({selectedSaldoInfo.itens.length} itens)
                  </span>

                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {selectedSaldoInfo.itens.length === 0 ? (
                      <p className="text-2xs text-slate-400 p-2 bg-white rounded-lg border">Nenhum item contratado nesta proposta.</p>
                    ) : (
                      selectedSaldoInfo.itens.map((it) => (
                        <div
                          key={it.id}
                          className="p-2.5 bg-white rounded-lg border border-slate-200 flex items-center justify-between text-xs gap-2"
                        >
                          <div className="flex items-center gap-2 truncate">
                            {it.categoria === 'ajudante' ? (
                              <Users className="w-4 h-4 text-blue-600 shrink-0" />
                            ) : it.categoria === 'veiculo' ? (
                              <Truck className="w-4 h-4 text-red-600 shrink-0" />
                            ) : (
                              <Tag className="w-4 h-4 text-slate-500 shrink-0" />
                            )}
                            <div className="truncate">
                              <p className="font-semibold text-slate-800 truncate">{it.servicos}</p>
                              <p className="text-[10px] text-slate-400">
                                Contratado: {it.quantidadeContratada} | Utilizado: {it.quantidadeUtilizada}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                it.statusSaldo === 'esgotado'
                                  ? 'bg-red-100 text-red-800'
                                  : it.statusSaldo === 'alerta'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              Saldo: {it.saldoRestante}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Comportamento Atual */}
                <div className="p-3 bg-white rounded-xl border border-slate-200 text-2xs space-y-1">
                  <span className="font-bold text-slate-700 block">Comportamento de Emissão:</span>
                  <p className="text-slate-600">
                    Ao abrir o formulário de OS para a proposta #{selectedSaldoInfo.pedidoId}, os menus dropdown de serviço e ajudante indicarão o saldo em tempo real de cada item. Caso o item selecionado esteja com saldo 0, {bloquearSemSaldo ? 'o sistema bloqueará o salvamento daquele item' : 'o sistema exibirá um banner de alerta informativo'}.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-xs text-slate-400">
                Selecione uma proposta à esquerda para inspecionar.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
